import { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Download, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import jsPDF from 'jspdf';

interface CVData {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  education: string;
  experience: string;
  skills: string;
  targetJob: string;
  targetCompany: string;
}

const CVBuilder = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cvData, setCvData] = useState<CVData>({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    education: '',
    experience: '',
    skills: '',
    targetJob: '',
    targetCompany: '',
  });
  const [enhancedData, setEnhancedData] = useState<{
    experience: string;
    coverLetter: string;
  } | null>(null);

  const handleInputChange = (field: keyof CVData, value: string) => {
    setCvData(prev => ({ ...prev, [field]: value }));
  };

  const enhanceWithAI = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enhance-cv', {
        body: { cvData }
      });

      if (error) throw error;

      setEnhancedData(data);
      toast({
        title: "Contenu amélioré avec succès !",
        description: "Votre CV et lettre de motivation ont été générés par l'IA.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le contenu. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = 20;

    // Header with name
    doc.setFontSize(24);
    doc.setTextColor(34, 139, 230);
    doc.text(cvData.fullName, margin, yPos);
    yPos += 10;

    // Contact info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`${cvData.email} | ${cvData.phone}`, margin, yPos);
    yPos += 5;
    doc.text(cvData.address, margin, yPos);
    yPos += 15;

    // Education
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('FORMATION', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const educationLines = doc.splitTextToSize(cvData.education, pageWidth - 2 * margin);
    doc.text(educationLines, margin, yPos);
    yPos += educationLines.length * 5 + 10;

    // Experience (AI enhanced)
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('EXPÉRIENCE PROFESSIONNELLE', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const experienceText = enhancedData?.experience || cvData.experience;
    const expLines = doc.splitTextToSize(experienceText, pageWidth - 2 * margin);
    doc.text(expLines, margin, yPos);
    yPos += expLines.length * 5 + 10;

    // Skills
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('COMPÉTENCES', margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const skillsLines = doc.splitTextToSize(cvData.skills, pageWidth - 2 * margin);
    doc.text(skillsLines, margin, yPos);

    // Save CV
    doc.save(`CV_${cvData.fullName.replace(/\s+/g, '_')}.pdf`);

    // Generate Cover Letter PDF
    if (enhancedData?.coverLetter) {
      const letterDoc = new jsPDF();
      let letterYPos = 20;

      // Header
      letterDoc.setFontSize(10);
      letterDoc.setTextColor(100, 100, 100);
      letterDoc.text(cvData.fullName, margin, letterYPos);
      letterYPos += 5;
      letterDoc.text(cvData.email, margin, letterYPos);
      letterYPos += 5;
      letterDoc.text(cvData.phone, margin, letterYPos);
      letterYPos += 15;

      // Date and company
      letterDoc.text(`Brazzaville, le ${new Date().toLocaleDateString('fr-FR')}`, margin, letterYPos);
      letterYPos += 10;
      letterDoc.text(`À l'attention de ${cvData.targetCompany}`, margin, letterYPos);
      letterYPos += 15;

      // Subject
      letterDoc.setFontSize(11);
      letterDoc.setTextColor(0, 0, 0);
      letterDoc.text(`Objet : Candidature au poste de ${cvData.targetJob}`, margin, letterYPos);
      letterYPos += 15;

      // Cover letter content
      letterDoc.setFontSize(10);
      letterDoc.setTextColor(60, 60, 60);
      const letterLines = letterDoc.splitTextToSize(enhancedData.coverLetter, pageWidth - 2 * margin);
      letterDoc.text(letterLines, margin, letterYPos);

      letterDoc.save(`Lettre_Motivation_${cvData.fullName.replace(/\s+/g, '_')}.pdf`);
    }

    toast({
      title: "PDF téléchargés !",
      description: "Votre CV et lettre de motivation ont été téléchargés.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Créateur de CV & Lettre <span className="text-primary">Intelligent</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Générez votre CV professionnel et lettre de motivation avec l'aide de l'IA
            </p>
          </div>

          <Card className="animate-fade-in-up animation-delay-200">
            <CardHeader>
              <CardTitle>Informations Personnelles</CardTitle>
              <CardDescription>Remplissez vos informations pour créer votre CV</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nom complet *</Label>
                  <Input
                    id="fullName"
                    value={cvData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={cvData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="jean.dupont@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    value={cvData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+242 06 123 4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={cvData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Brazzaville, Congo"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="education">Formation *</Label>
                <Textarea
                  id="education"
                  value={cvData.education}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  placeholder="Ex: Licence en Informatique - Université Marien Ngouabi (2020-2023)"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience">Expérience Professionnelle *</Label>
                <Textarea
                  id="experience"
                  value={cvData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  placeholder="Décrivez vos expériences professionnelles..."
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="skills">Compétences *</Label>
                <Textarea
                  id="skills"
                  value={cvData.skills}
                  onChange={(e) => handleInputChange('skills', e.target.value)}
                  placeholder="Ex: JavaScript, React, Node.js, Communication, Travail d'équipe"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="targetJob">Poste visé *</Label>
                  <Input
                    id="targetJob"
                    value={cvData.targetJob}
                    onChange={(e) => handleInputChange('targetJob', e.target.value)}
                    placeholder="Ex: Développeur Web"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="targetCompany">Entreprise cible</Label>
                  <Input
                    id="targetCompany"
                    value={cvData.targetCompany}
                    onChange={(e) => handleInputChange('targetCompany', e.target.value)}
                    placeholder="Ex: Tech Solutions Congo"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  onClick={enhanceWithAI}
                  disabled={loading || !cvData.fullName || !cvData.email || !cvData.experience}
                  className="flex-1"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Améliorer avec l'IA
                    </>
                  )}
                </Button>
                
                {enhancedData && (
                  <Button
                    onClick={generatePDF}
                    variant="secondary"
                    className="flex-1"
                    size="lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Télécharger PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {enhancedData && (
            <div className="mt-8 space-y-6 animate-fade-in-up">
              <Card>
                <CardHeader>
                  <CardTitle>Expérience Professionnelle Améliorée</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {enhancedData.experience}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lettre de Motivation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {enhancedData.coverLetter}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CVBuilder;
